import React, { useState } from 'react';
import { Modal } from 'antd';
import {
  createCryptoPayoutDetails,
  CryptoPayoutDetails,
} from '../PayoutDetails';
import { Spinner } from '../../../shared/ui/Spinner/Spinner';
import {
  getTokenHumanReadableName,
  NETWORK_TYPES,
  NetworkLogoMap,
  TokenLogoMap,
  upperCaseNetworkType,
} from 'features/bill/lib';
import { PaymentInformation } from 'widgets/Payments/PaymentModal.PaymentInformation';
import {
  MakeCryptoTransferButton,
  useTokenBalanceByWalletProvider,
} from '../MakeCryptoTransferButton';
import { WarningsSection } from 'widgets/Payments/PaymentModal.WarningsSection';
import { ConfirmationSection } from 'widgets/Payments/PaymentModal.ConfirmationSection';
import { sendTransactionLog } from 'features/analytics/sendTransactionLog';
import { Button } from '../../../shared/ui/Button/Button';
import { useAccount } from 'wagmi';
import { useAuth0 } from '@auth0/auth0-react';
import { ConfirmInWalletOverlay } from 'widgets/Payments/PaymentModal.ConfirmInWalletOverlay';
import {
  FailedPayment,
  SuccessConfirmation,
} from 'widgets/Payments/CryptoToFiatPaymentModal.SuccessConfirmation';
import { useTransfers } from 'features/transfers/useTransfers';
import {
  useInitiateCrossChainTransfer,
  useInitiateCrossChainTransferByInvoice,
} from './crossChain/useInitiateCrossChainTransfer';
import {
  useCompleteCrossChainTransfer,
  useCompleteCrossChainTransferByInvoice,
} from './crossChain/useCompleteCrossChainTransfer';
import { useBillMutations, useBills } from 'entities/bill/model';
import { SourceSelection } from './crossChain/SourceSelection';

const CrossChainTransferModal: React.FC<{
  show: boolean;
  initiatingTransfer: boolean;
  initiateTransferData: {
    depositInfo: {
      token: string;
      chain: string;
      amount: string;
      fees: string;
      address: string;
    };
  };
  completingTransfer: boolean;
  completeTransferSuccess: boolean;
  completeTransferError: boolean;
  initiateTransferError: boolean;
  title: string;
  onClose: () => void;
  onSourceNetworkTokenChange?: ({
    sourceNetwork,
    sourceToken,
  }: {
    sourceNetwork: string;
    sourceToken: string;
  }) => void;
  onCompleteTransfer: (opt: { transactionHash: string }) => void;
  payouts: CryptoPayoutDetails[];
}> = ({
  show,
  completingTransfer,
  completeTransferSuccess,
  completeTransferError,
  initiateTransferData,
  title,
  payouts,
  onClose,
  initiatingTransfer,
  onCompleteTransfer,
  onSourceNetworkTokenChange,
  initiateTransferError,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [
    waitingForUserWalletConfirmation,
    setWaitingForUserWalletConfirmation,
  ] = useState(false);
  const { chain } = useAccount();
  const [isConfirmedByUser, setIsConfirmedByUser] = useState(false);
  const [isCryptoTransferError, setIsCryptoTransferError] = useState(false);

  const _getBalanceWalletProvider = () => {
    if (payouts[0] && payouts[0].sourceNetwork === NETWORK_TYPES.Solana) {
      return 'solana-wallet-connect';
    }
    return 'wallet-connect';
  };

  const { getReadableBalanceString } = useTokenBalanceByWalletProvider(
    _getBalanceWalletProvider(),
    initiateTransferData?.depositInfo?.token,
    initiateTransferData?.depositInfo?.chain
  );

  const _handleConfirm = (val: boolean) => {
    setIsConfirmedByUser(val);
  };

  const _handleCryptoTransactionSuccess = ({
    transactionHash,
  }: {
    transactionHash: string;
  }) => {
    sendTransactionLog(getAccessTokenSilently, {
      title: '[Offramp][Crypto Deposit] - Success',
      transactionHash,
    });

    onCompleteTransfer({
      transactionHash: transactionHash,
    });

    setWaitingForUserWalletConfirmation(false);
  };

  const _handleCryptoTransactionStart = () => {
    setWaitingForUserWalletConfirmation(true);
  };

  const _renderTransactionDetails = () => {
    if (initiatingTransfer) {
      return (
        <div className="flex items-center justify-center px-6 py-4 min-h-[300px]">
          <Spinner size="xl" color="indigo" />
        </div>
      );
    }

    return (
      <>
        {payouts.map((p) => (
          <div className="mt-6 mb-6">
            <div className="flex flex-col items-start">
              <div className="text-xs font-normal text-slate-400">
                Recipient
              </div>
              <div className="mt-1 mb-1 text-sm font-medium text-slate-300">
                {p.walletAddress}
              </div>
              <div className="flex items-center text-xs text-slate-400">
                <div className="mr-1 h-4 w-4">
                  <img
                    src={NetworkLogoMap[upperCaseNetworkType(p.network)]}
                    alt=""
                    aria-hidden="true"
                  />
                </div>
                {p.network}
              </div>
            </div>

            <div className="mt-4 flex flex-col">
              <div className="mb-1 flex items-end justify-between">
                <div>
                  <div className=" text-left text-xs font-normal text-slate-400">
                    Token
                  </div>

                  <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                    <div className="mr-1 h-4 w-4">
                      <img
                        src={TokenLogoMap[p.currency]}
                        alt=""
                        aria-hidden="true"
                      />
                    </div>
                    <div className=" text-sm font-medium text-slate-300">
                      {getTokenHumanReadableName(p.currency)}
                    </div>
                  </div>
                </div>
                <div className="text-sm font-medium text-slate-300">
                  {p.amountStr}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="my-4 border-b border-slate-700"></div>

        {initiateTransferData && (
          <>
            <PaymentInformation
              usdValue=""
              showRecipientLabel={false}
              title="Deposit Information"
              walletAddress={initiateTransferData?.depositInfo?.address}
              network={initiateTransferData?.depositInfo?.chain}
              token={initiateTransferData?.depositInfo?.token}
              amount={initiateTransferData?.depositInfo?.amount}
              balance={getReadableBalanceString()}
            />
            <WarningsSection
              showNetworkMismatch={
                initiateTransferData.depositInfo.chain !==
                NETWORK_TYPES.Solana.toLowerCase()
              }
              connectedChain={chain}
              payeeToken={initiateTransferData?.depositInfo?.token}
              payeeNetwork={initiateTransferData?.depositInfo?.chain}
              payeeAmount={initiateTransferData?.depositInfo?.amount}
              payeeWalletAddress={initiateTransferData?.depositInfo?.address}
              paymentProps={{
                balance: getReadableBalanceString(),
              }}
            />
          </>
        )}

        <ConfirmationSection
          isConfirmed={isConfirmedByUser}
          setIsConfirmed={() => {}}
          onConfirmationChange={_handleConfirm}
        />

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          {_getBalanceWalletProvider() && (
            <MakeCryptoTransferButton
              cryptoPaymentProviderId={_getBalanceWalletProvider()}
              onTransferFail={({ error }: { error?: any } = {}) => {
                sendTransactionLog(getAccessTokenSilently, {
                  title: '[Offramp][Crypto Deposit] - Fail',
                  error,
                });
                setWaitingForUserWalletConfirmation(false);
                setIsCryptoTransferError(true);
              }}
              disabled={!isConfirmedByUser}
              onTransferSuccess={_handleCryptoTransactionSuccess}
              onTransferStart={_handleCryptoTransactionStart}
              payouts={[
                createCryptoPayoutDetails(
                  initiateTransferData?.depositInfo?.amount,
                  initiateTransferData?.depositInfo?.token,
                  initiateTransferData?.depositInfo?.chain,
                  initiateTransferData?.depositInfo?.address
                ),
              ]}
            />
          )}

          <Button color="gray" className="sm:order-first" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </>
    );
  };
  const _renderMiddleStepScreen = () => {
    if (payouts[0] && !payouts[0].hasSource()) {
      return (
        <SourceSelection
          onSelect={({ sourceNetwork, sourceToken }) => {
            onSourceNetworkTokenChange({ sourceNetwork, sourceToken });
          }}
        />
      );
    }

    if (completingTransfer || waitingForUserWalletConfirmation) {
      return <ConfirmInWalletOverlay show />;
    }

    if (completeTransferSuccess) {
      return <SuccessConfirmation onClose={onClose} show />;
    }

    if (
      completeTransferError ||
      initiateTransferError ||
      isCryptoTransferError
    ) {
      return <FailedPayment onClose={onClose} show />;
    }
  };

  return (
    <Modal
      title={title}
      open={show}
      footer={null}
      centered
      maskClosable={false}
      destroyOnClose
      onCancel={onClose}>
      {_renderMiddleStepScreen()}
      {_renderTransactionDetails()}
    </Modal>
  );
};

export const TransferPaymentViaCrossChainTransferModal: React.FC<{
  show: boolean;
  transfers: Record<string, any>[];
  payouts: CryptoPayoutDetails[];
  onClose: () => void;
}> = ({ show, onClose, payouts, transfers }) => {
  const { invalidate } = useTransfers();
  const {
    data: initiateTransferData,
    isPending: initiatingTransfer,
    isError: initiateTransferError,
  } = useInitiateCrossChainTransfer({
    transferIds: transfers.map((tr) => tr.id),
  });
  const {
    isPending: completingTransfer,
    isError: completeTransferError,
    isSuccess: completeTransferSuccess,
    mutateAsync,
  } = useCompleteCrossChainTransfer();

  const handleCompleteTransfer = ({
    transactionHash,
  }: {
    transactionHash: string;
  }) => {
    if (!completingTransfer) {
      mutateAsync({
        transferIds: transfers.map((tr) => tr.id),
        transactionHash,
      }).then(() => {
        return invalidate();
      });
    }
  };

  return (
    <CrossChainTransferModal
      completingTransfer={completingTransfer}
      completeTransferSuccess={completeTransferSuccess}
      completeTransferError={completeTransferError}
      initiateTransferError={initiateTransferError}
      onCompleteTransfer={handleCompleteTransfer}
      initiateTransferData={initiateTransferData}
      initiatingTransfer={initiatingTransfer}
      title={'Transfer Payment'}
      payouts={payouts}
      show={show}
      onClose={onClose}
    />
  );
};

export const InvoiceReceiverPaymentViaCrossChainTransferModal: React.FC<{
  show: boolean;
  bills: Record<string, any>[];
  payouts: CryptoPayoutDetails[];
  onClose: () => void;
}> = ({ show, onClose, payouts, bills }) => {
  const [sourceNetwork, setSourceNetwork] = useState<string>();
  const [sourceToken, setSourceToken] = useState<string>();

  /** Enrich bills data */
  const { data: enrichedBills } = useBills(
    bills?.filter((b) => !!b).map((b) => b?._id)
  );
  const { invalidate } = useBillMutations();
  const {
    data: initiateTransferData,
    isPending: initiatingTransfer,
    isError: initiateTransferError,
  } = useInitiateCrossChainTransferByInvoice({
    invoiceIds: enrichedBills?.map((b) => b.invoice.id) || [],
    sourceNetwork,
    sourceToken,
  });
  const {
    isPending: completingTransfer,
    isError: completeTransferError,
    isSuccess: completeTransferSuccess,
    mutateAsync,
  } = useCompleteCrossChainTransferByInvoice();

  const handleCompleteTransfer = ({
    transactionHash,
  }: {
    transactionHash: string;
  }) => {
    if (!completingTransfer) {
      mutateAsync({
        invoiceIds: enrichedBills?.map((b) => b.invoice.id) || [],
        transactionHash,
      }).then(() => {
        return invalidate();
      });
    }
  };

  return (
    <CrossChainTransferModal
      completingTransfer={completingTransfer}
      completeTransferSuccess={completeTransferSuccess}
      completeTransferError={completeTransferError}
      initiateTransferError={initiateTransferError}
      onSourceNetworkTokenChange={({
        sourceNetwork: newSourceNetwork,
        sourceToken: newSourceToken,
      }) => {
        setSourceNetwork(newSourceNetwork);
        setSourceToken(newSourceToken);
      }}
      onCompleteTransfer={handleCompleteTransfer}
      initiateTransferData={initiateTransferData}
      initiatingTransfer={initiatingTransfer}
      title={'Bill Payment'}
      payouts={(payouts || []).map((p) => {
        if (!p.hasSource() && sourceNetwork && sourceToken) {
          p.setSourceNetwork(sourceNetwork);
          p.setSourceCurrency(sourceToken);
        }
        return p;
      })}
      show={show}
      onClose={onClose}
    />
  );
};
